/**
 * PC Parts Builder
 *
 * Class-based interactive builder.
 *   - Reads pre-rendered product JSON from <script type="application/json" data-pc-builder-products>
 *     (eliminates the 30-item /products.json cap)
 *   - Phase 1: product images, progress bar, skeleton loaders, sticky modal search,
 *              backdrop blur, sticky-mobile summary, empty-state icons
 *   - Phase 2: inventory awareness, 250ms debounced search, variant dropdown in modal,
 *              dynamic currency via theme.moneyFormat + formatMoney()
 *   - Phase 4: shareable URLs (URLSearchParams + history.replaceState),
 *              configurable upsells, cart-drawer integration
 *   - Metafield-only socket compatibility via product.metafields.custom.socket_type
 *   - Preserves save/load integration (data-component="<id>" hooks + change events)
 */

(function () {
  "use strict";

  const SECTION_SELECTOR = ".pc-builder";

  // ----- Utilities -----

  // Theme-compatible debounce; falls back if the global one isn't present.
  const debounce =
    typeof window.debounce === "function"
      ? window.debounce
      : function (fn, wait) {
          let t;
          return function () {
            const args = arguments;
            clearTimeout(t);
            t = setTimeout(() => fn.apply(this, args), wait);
          };
        };

  // Uses theme's global formatMoney() if available; otherwise a basic fallback.
  function money(cents, fallbackFormat) {
    if (typeof window.formatMoney === "function") {
      try {
        return window.formatMoney(cents, fallbackFormat);
      } catch (e) {
        /* fall through */
      }
    }
    const amt = (parseInt(cents, 10) / 100).toFixed(2);
    return "$" + amt.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  function escapeHtml(str) {
    if (str == null) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // Minimal focus trap helper (theme's trapFocus is not globally exposed in all builds).
  function focusTrap(container) {
    const selector =
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const onKey = (e) => {
      if (e.key !== "Tab") return;
      const focusables = Array.from(container.querySelectorAll(selector)).filter(
        (el) => !el.hasAttribute("disabled") && el.offsetParent !== null
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener("keydown", onKey);
    return () => container.removeEventListener("keydown", onKey);
  }

  // ----- PCBuilder class -----

  class PCBuilder {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.moneyFormat = root.dataset.moneyFormat || "${{amount}}";
      this.shareableUrls = root.dataset.shareableUrls === "true";

      // UI refs
      this.componentsList = root.querySelector("#componentsList");
      this.summaryEl = root.querySelector("[data-summary]");
      this.summaryCountEl = root.querySelector("[data-summary-count]");
      this.summaryTotalEls = root.querySelectorAll("[data-summary-total]");
      this.summaryMsgsEl = root.querySelector("[data-summary-messages]");
      this.summaryToastEl = root.querySelector("[data-toast]");
      this.addBtn = root.querySelector("[data-add-to-cart]");
      this.addToCartLabel = root.querySelector("[data-add-to-cart-label]");
      this.progressEl = root.querySelector("[data-progress]");
      this.progressSelectedEl = root.querySelector("[data-progress-selected]");
      this.progressTotalEl = root.querySelector("[data-progress-total]");
      this.progressFillEl = root.querySelector("[data-progress-fill]");

      // Modal refs
      this.modalOverlay = root.querySelector("[data-modal-overlay]");
      this.modalEl = root.querySelector("[data-modal]");
      this.modalTitleEl = root.querySelector("[data-modal-title]");
      this.modalSearchEl = root.querySelector("[data-modal-search]");
      this.modalProductsEl = root.querySelector("[data-modal-products]");
      this.modalCloseBtn = root.querySelector("[data-modal-close]");

      this.releaseFocusTrap = null;
      this.lastOpener = null;

      // State
      this.components = this.readComponentBlocks();
      this.productsByComponent = this.readPreRenderedProducts();
      this.selections = {};
      this.activeModalVariants = {};
      this.currentComponentId = null;
      this._celebrated = false;

      // Wattage Warrior
      this.wattageBox = this.root.querySelector("[data-wattage-box]");
      this.totalWattageEl = this.root.querySelector("[data-total-wattage]");
      this.wattageFillEl = this.root.querySelector("[data-wattage-fill]");
      this.wattageWarningEl = this.root.querySelector("[data-wattage-warning]");

      // Mobile Capsule
      this.capsuleEl = this.root.querySelector("[data-capsule]");
      this.capsuleRingEl = this.root.querySelector("[data-capsule-ring]");
      this.capsulePercentEl = this.root.querySelector("[data-capsule-percent]");
      this.capsuleTotalEl = this.root.querySelector("[data-capsule-total]");

      // Naming & Audit
      this.buildNameInput = this.root.querySelector("[data-build-name]");
      this.certifiedBadge = this.root.querySelector("[data-certified-badge]");
      this.headerSteps = this.root.querySelectorAll(".hero-steps-tracker .tracker-step");

      this.bindEvents();
      this.hydrateFromQueryString();
      this.render();
      this.groupOptionalRows();

      // Expose public API for save/load integration.
      window.pcBuilder = window.pcBuilder || {};
      window.pcBuilder.applyBuild = this.applyBuildFromSaves.bind(this);
      window.pcBuilder.instance = this;
    }

    readComponentBlocks() {
      return Array.from(
        this.componentsList.querySelectorAll("[data-component-row]")
      ).map((row) => {
        const id = row.dataset.componentRow;
        const contentEl = row.querySelector("[data-component-content]");
        const btn = row.querySelector("[data-open-modal]");
        return {
          id,
          label: btn ? btn.dataset.componentLabel : id,
          required: row.dataset.componentRequired === "true",
          row,
          contentEl,
        };
      });
    }

    readPreRenderedProducts() {
      const out = {};
      this.root
        .querySelectorAll("script[data-pc-builder-products]")
        .forEach((script) => {
          const id = script.dataset.pcBuilderProducts;
          try {
            out[id] = JSON.parse(script.textContent || "[]");
          } catch (e) {
            console.error("[pc-builder] failed to parse product JSON for", id, e);
            out[id] = [];
          }
        });
      return out;
    }

    bindEvents() {
      // Open modal buttons (delegated; re-rendered content keeps them wired).
      this.root.addEventListener("click", (e) => {
        const opener = e.target.closest("[data-open-modal]");
        if (opener && this.root.contains(opener)) {
          e.preventDefault();
          this.openModal(opener.dataset.componentId, opener);
          return;
        }
        const remover = e.target.closest("[data-remove-component]");
        if (remover && this.root.contains(remover)) {
          e.preventDefault();
          this.removeComponent(remover.dataset.componentId);
          return;
        }
      });

      // Modal close
      this.modalCloseBtn.addEventListener("click", () => this.closeModal());
      this.modalOverlay.addEventListener("click", (e) => {
        if (e.target === this.modalOverlay) this.closeModal();
      });
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.modalOverlay.classList.contains("is-open")) {
          this.closeModal();
        }
        // Ctrl+Enter / Cmd+Enter — Add to Cart shortcut (P7)
        if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
          if (!this.modalOverlay.classList.contains("is-open") && this.addBtn && !this.addBtn.disabled) {
            e.preventDefault();
            this.addAllToCart();
          }
        }
      });

      // Swipe-to-dismiss modal (P4) — downward swipe >= 80px closes modal
      let _swipeStartY = null;
      this.modalOverlay.addEventListener("touchstart", (e) => {
        _swipeStartY = e.touches[0].clientY;
      }, { passive: true });
      this.modalOverlay.addEventListener("touchmove", (e) => {
        if (_swipeStartY === null) return;
        if (e.touches[0].clientY - _swipeStartY > 80) {
          _swipeStartY = null;
          this.closeModal();
        }
      }, { passive: true });
      this.modalOverlay.addEventListener("touchend", () => { _swipeStartY = null; }, { passive: true });

      // Debounced modal search — 300ms
      const onSearch = debounce((value) => {
        this.renderModalProducts(value);
      }, 300);
      this.modalSearchEl.addEventListener("input", (e) => onSearch(e.target.value));

      // Add to cart
      if (this.addBtn) {
        this.addBtn.addEventListener("click", () => this.addAllToCart());
      }

      // Upsell selection total recalc
      this.root.querySelectorAll("[data-upsell]").forEach((cb) => {
        cb.addEventListener("change", () => this.renderSummary());
      });

      // Master Templates
      this.root.addEventListener("click", (e) => {
        const templateBtn = e.target.closest("[data-apply-template]");
        if (templateBtn && this.root.contains(templateBtn)) {
          e.preventDefault();
          const build = JSON.parse(templateBtn.dataset.templateData);
          this.applyBuildFromSaves(build);
          this.fireCelebration();
        }
      });

      // Social Share
      this.root.addEventListener("click", (e) => {
        const whatsappBtn = e.target.closest("[data-share-whatsapp]");
        if (whatsappBtn) {
          const name = this.buildNameInput ? (this.buildNameInput.value || "Custom PC") : "Custom PC";
          const text = encodeURIComponent(`Check out '${name}', my custom PC build at NDX Computer! ${window.location.href}`);
          window.open(`https://wa.me/?text=${text}`, "_blank");
        }
        const copyBtn = e.target.closest("[data-share-copy]");
        if (copyBtn) {
          navigator.clipboard.writeText(window.location.href).then(() => {
            const label = copyBtn.querySelector("[data-copy-text]");
            const original = label.textContent;
            label.textContent = "Copied!";
            copyBtn.classList.add("is-success");
            setTimeout(() => {
              label.textContent = original;
              copyBtn.classList.remove("is-success");
            }, 2000);
          });
        }
      });

      // Capsule scroll visibility
      window.addEventListener("scroll", () => {
        if (!this.summaryCountEl) return;
        const summaryRect = this.summaryCountEl.getBoundingClientRect();
        if (summaryRect.bottom < 0) {
          this.capsuleEl.hidden = false;
        } else {
          this.capsuleEl.hidden = true;
        }
      });

      // Build Name persistence
      if (this.buildNameInput) {
        this.buildNameInput.addEventListener("input", debounce(() => {
          this.updateQueryString();
        }, 500));
      }
    }

    // ----- Modal flow -----

    openModal(componentId, opener) {
      this.currentComponentId = componentId;
      this.lastOpener = opener || null;
      const comp = this.components.find((c) => c.id === componentId);
      this.modalTitleEl.textContent = `Select ${comp ? comp.label : "Component"}`;
      this.modalSearchEl.value = "";
      this.modalOverlay.classList.add("is-open");
      this.modalOverlay.setAttribute("aria-hidden", "false");
      document.body.style.overflow = "hidden";

      // Skeleton loader briefly (Phase 1 visual polish; data is already synchronous).
      this.renderSkeletons();
      requestAnimationFrame(() => {
        this.renderModalProducts("");
        this.modalSearchEl.focus();
      });

      // Focus trap
      this.releaseFocusTrap = focusTrap(this.modalEl);
    }

    closeModal() {
      this.modalOverlay.classList.remove("is-open");
      this.modalOverlay.setAttribute("aria-hidden", "true");
      document.body.style.overflow = "";
      this.currentComponentId = null;
      if (this.releaseFocusTrap) {
        this.releaseFocusTrap();
        this.releaseFocusTrap = null;
      }
      if (this.lastOpener && typeof this.lastOpener.focus === "function") {
        this.lastOpener.focus();
      }
    }

    renderSkeletons() {
      const html = Array.from({ length: 5 })
        .map(
          () => `
            <div class="skeleton-row">
              <div class="skeleton-block skeleton-block--image"></div>
              <div class="skeleton-block--body">
                <div class="skeleton-block skeleton-block--line skeleton-block--title"></div>
                <div class="skeleton-block skeleton-block--line"></div>
              </div>
            </div>`
        )
        .join("");
      this.modalProductsEl.innerHTML = html;
    }

    renderModalProducts(searchTerm) {
      const componentId = this.currentComponentId;
      if (!componentId) return;
      const products = this.productsByComponent[componentId] || [];
      const term = (searchTerm || "").trim().toLowerCase();
      const filtered = term
        ? products.filter((p) => p.title.toLowerCase().includes(term))
        : products;

      if (!filtered.length) {
        this.modalProductsEl.innerHTML = `<div class="no-results">No products found</div>`;
        return;
      }

      this.modalProductsEl.innerHTML = filtered
        .map((p) => this.productItemHtml(p))
        .join("");

      // Wire click / variant change for each row.
      this.modalProductsEl.querySelectorAll("[data-product-id]").forEach((row) => {
        const productId = row.dataset.productId;

        // Variant dropdown sync
        const variantSelect = row.querySelector("[data-variant-select]");
        if (variantSelect) {
          variantSelect.addEventListener("click", (e) => e.stopPropagation());
          variantSelect.addEventListener("change", (e) => {
            e.stopPropagation();
            this.activeModalVariants[productId] = e.target.value;
            this.updateProductRowState(row, productId);
          });
          // Seed default
          if (!this.activeModalVariants[productId]) {
            this.activeModalVariants[productId] = variantSelect.value;
          }
        }

        // Select button
        const selectBtn = row.querySelector("[data-select-product]");
        if (selectBtn) {
          selectBtn.addEventListener("click", (e) => {
            e.stopPropagation();
            if (selectBtn.disabled) return;
            this.selectProduct(componentId, productId);
          });
        }

        // Whole-row click (when no variant dropdown) to select.
        row.addEventListener("click", (e) => {
          if (e.target.closest("[data-variant-select]")) return;
          if (row.classList.contains("is-disabled")) return;
          this.selectProduct(componentId, productId);
        });
      });
    }

    productItemHtml(product) {
      const hasMultipleVariants = product.variants && product.variants.length > 1;
      const selectedVariantId =
        this.activeModalVariants[product.id] ||
        (product.variants && product.variants[0] && product.variants[0].id);
      const selectedVariant =
        (product.variants || []).find((v) => String(v.id) === String(selectedVariantId)) ||
        (product.variants && product.variants[0]);
      const price = selectedVariant ? selectedVariant.price : product.price;
      const compareAtPrice = selectedVariant ? selectedVariant.compare_at_price : product.compare_at_price;
      const isOnSale = compareAtPrice && compareAtPrice > price;
      const available = product.available !== false;

      const imageHtml = product.image
        ? `<img class="product-item__image" src="${escapeHtml(
            product.image
          )}" alt="${escapeHtml(product.title)}" loading="lazy" width="56" height="56">`
        : `<div class="product-item__image-placeholder"></div>`;

      const variantHtml = hasMultipleVariants
        ? `<select class="product-item__variant-select" data-variant-select aria-label="Variant">
             ${product.variants
               .map(
                 (v) =>
                   `<option value="${v.id}" ${
                     String(v.id) === String(selectedVariantId) ? "selected" : ""
                   } ${!v.available ? "disabled" : ""}>${escapeHtml(v.title)}${
                     !v.available ? " (Out of Stock)" : ""
                   }</option>`
               )
               .join("")}
           </select>`
        : "";

      const oosBadge = !available
        ? `<span class="product-item__oos-badge">Out of Stock</span>`
        : "";

      const priceHtml = isOnSale
        ? `<div class="product-item__price-group">
             <span class="product-item__price product-item__price--sale" data-price-display>${money(price, this.moneyFormat)}</span>
             <span class="product-item__compare-price">${money(compareAtPrice, this.moneyFormat)}</span>
           </div>
           <span class="product-item__sale-badge">Sale</span>`
        : `<span class="product-item__price" data-price-display>${money(price, this.moneyFormat)}</span>`;

      return `
        <div class="product-item ${!available ? "is-disabled" : ""}" data-product-id="${product.id}">
          ${imageHtml}
          <div class="product-item__body">
            <h3 class="product-item__title">${escapeHtml(product.title)}</h3>
            <div class="product-item__meta">
              ${priceHtml}
              ${oosBadge}
            </div>
          </div>
          <div class="product-item__actions">
            ${variantHtml}
            <button type="button" class="btn btn-primary" data-select-product ${
              !available ? "disabled" : ""
            }>Select</button>
          </div>
        </div>`;
    }

    updateProductRowState(row, productId) {
      const products = this.productsByComponent[this.currentComponentId] || [];
      const product = products.find((p) => String(p.id) === String(productId));
      if (!product) return;
      const variantId = this.activeModalVariants[productId];
      const variant = (product.variants || []).find(
        (v) => String(v.id) === String(variantId)
      );
      if (!variant) return;
      const isOnSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      const priceEl = row.querySelector("[data-price-display]");
      if (priceEl) {
        priceEl.textContent = money(variant.price, this.moneyFormat);
        priceEl.classList.toggle("product-item__price--sale", isOnSale);
      }
      const comparePriceEl = row.querySelector(".product-item__compare-price");
      if (isOnSale) {
        if (comparePriceEl) {
          comparePriceEl.textContent = money(variant.compare_at_price, this.moneyFormat);
        } else {
          const priceGroup = priceEl?.parentElement;
          if (priceGroup && priceGroup.classList.contains("product-item__price-group")) {
            const newCompareEl = document.createElement("span");
            newCompareEl.className = "product-item__compare-price";
            newCompareEl.textContent = money(variant.compare_at_price, this.moneyFormat);
            priceGroup.appendChild(newCompareEl);
          }
        }
      } else if (comparePriceEl) {
        comparePriceEl.remove();
      }
      const saleEl = row.querySelector(".product-item__sale-badge");
      if (isOnSale && !saleEl) {
        const meta = row.querySelector(".product-item__meta");
        if (meta) {
          const badge = document.createElement("span");
          badge.className = "product-item__sale-badge";
          badge.textContent = "Sale";
          meta.appendChild(badge);
        }
      } else if (!isOnSale && saleEl) {
        saleEl.remove();
      }
      const selectBtn = row.querySelector("[data-select-product]");
      if (selectBtn) selectBtn.disabled = false;
      row.classList.toggle("is-disabled", false);
      const oldBadge = row.querySelector(".product-item__oos-badge");
      if (oldBadge) oldBadge.remove();
      if (!variant.available) {
        const meta = row.querySelector(".product-item__meta");
        if (meta) {
          const b = document.createElement("span");
          b.className = "product-item__oos-badge";
          b.textContent = "Out of stock";
          meta.appendChild(b);
        }
      }
    }

    // ----- Selection state -----

    selectProduct(componentId, productId) {
      const products = this.productsByComponent[componentId] || [];
      const product = products.find((p) => String(p.id) === String(productId));
      if (!product) return;
      const variantId =
        this.activeModalVariants[productId] ||
        (product.variants && product.variants[0] && product.variants[0].id);
      const variant =
        (product.variants || []).find((v) => String(v.id) === String(variantId)) ||
        (product.variants && product.variants[0]);
      if (!variant) return;

      this.selections[componentId] = {
        productId: product.id,
        variantId: variant.id,
        title: product.title,
        image: product.image,
        price: variant.price,
        compare_at_price: variant.compare_at_price || null,
        socket: product.socket || null,
        ram_type: product.ram_type || null,
        wattage: product.wattage || 0,
        handle: product.handle,
        url: product.url,
      };
      // Attach compatibility info (metafield-based only).
      this.refreshCompatibility();
      this.closeModal();
      this.render();
      this.updateQueryString();
      this.emitChangeForSaves(componentId);
      // P5: GA4 tracking – component selected
      this._trackEvent('select_item', {
        item_list_name: 'PC Builder',
        items: [{ item_id: String(product.id), item_name: product.title, item_category: componentId, price: (variant.price / 100) }]
      });
    }

    emitChangeForSaves(componentId) {
      // Custom event for pc-builder-saves.js listener on document
      document.dispatchEvent(new CustomEvent("pc-builder:selection-change", {
        detail: { componentId, selections: this.selections }
      }));
      // Also dispatch element-level change event for legacy listeners
      const el = this.root.querySelector(`[data-component="${componentId}"]`);
      if (el) el.dispatchEvent(new Event("change", { bubbles: true }));
    }

    removeComponent(componentId) {
      delete this.selections[componentId];
      this.refreshCompatibility();
      this.render();
      this.updateQueryString();
      this.emitChangeForSaves(componentId);
    }

    refreshCompatibility() {
      // Clear any existing warnings (Compatibility checks removed per user request)
      Object.values(this.selections).forEach((s) => (s.warning = null));
    }

    // ----- Rendering -----

    render() {
      this.renderComponentRows();
      this.renderSummary();
      this.renderProgress();
    }

    renderComponentRows() {
      this.components.forEach((comp) => {
        const selected = this.selections[comp.id];
        const row = comp.row;
        row.classList.toggle("is-selected", !!selected);

        if (selected) {
          const thumbHtml = selected.image
            ? `<img class="selected-item__thumb" src="${escapeHtml(selected.image)}" alt="${escapeHtml(selected.title)}" loading="lazy" width="40" height="40">`
            : `<div class="selected-item__thumb selected-item__thumb--placeholder" aria-hidden="true"></div>`;

          const isOnSale = selected.compare_at_price && selected.compare_at_price > selected.price;
          const itemSavings = isOnSale ? (selected.compare_at_price - selected.price) : 0;
          const saveBadgeHtml = itemSavings > 0
            ? `<span class="selected-item__save-badge">Save ${money(itemSavings, this.moneyFormat)}</span>`
            : "";
          const pricesHtml = isOnSale
            ? `<div class="selected-item__prices">
                 <span class="selected-item__price selected-item__price--sale">${money(selected.price, this.moneyFormat)}</span>
                 <span class="selected-item__compare-price-inline">${money(selected.compare_at_price, this.moneyFormat)}</span>
               </div>`
            : `<div class="selected-item__prices"><span class="selected-item__price">${money(selected.price, this.moneyFormat)}</span></div>`;

          comp.contentEl.innerHTML = `
            <div class="selected-item selected-item--compact"
                 data-component="${escapeHtml(comp.id)}"
                 data-variant-id="${escapeHtml(String(selected.variantId))}"
                 data-product-id="${escapeHtml(String(selected.productId))}">
              ${thumbHtml}
              <div class="selected-item__name">${escapeHtml(selected.title)}</div>
              ${saveBadgeHtml}
              ${pricesHtml}
              <div class="selected-item__inline-actions">
                <button type="button" class="btn btn-sm btn-secondary" data-open-modal
                  data-component-id="${escapeHtml(comp.id)}"
                  data-component-label="${escapeHtml(comp.label)}">Change</button>
                <button type="button" class="btn btn-sm btn-danger" data-remove-component
                  data-component-id="${escapeHtml(comp.id)}" aria-label="Remove ${escapeHtml(comp.label)}">✕</button>
              </div>
            </div>`;
        } else {
          comp.contentEl.innerHTML = `
            <button
              type="button"
              class="btn btn-primary"
              data-open-modal
              data-component-id="${escapeHtml(comp.id)}"
              data-component-label="${escapeHtml(comp.label)}"
            >+ Choose ${escapeHtml(comp.label)}</button>`;
        }
      });
    }

    groupOptionalRows() {
      const optionalRows = Array.from(
        this.componentsList.querySelectorAll('[data-collapsed-default="true"]')
      );
      if (!optionalRows.length) return;

      // Group rows by their data-collapse-group attribute, preserving DOM order.
      const groupMap = new Map();
      optionalRows.forEach((row) => {
        const groupName = row.dataset.collapseGroup || "More Components";
        if (!groupMap.has(groupName)) groupMap.set(groupName, []);
        groupMap.get(groupName).push(row);
      });

      // Create one collapsible section per group.
      groupMap.forEach((rows, groupName) => {
        const wrapper = document.createElement("div");
        wrapper.className = "optional-components";
        wrapper.innerHTML = `
          <button type="button" class="optional-toggle" data-optional-toggle aria-expanded="false">
            <span class="optional-toggle__icon" aria-hidden="true">+</span>
            <span>${escapeHtml(groupName)}</span>
          </button>
          <div class="optional-rows" data-optional-rows hidden></div>
        `;

        const rowsContainer = wrapper.querySelector("[data-optional-rows]");
        rows.forEach((row) => rowsContainer.appendChild(row));
        this.componentsList.appendChild(wrapper);

        wrapper.querySelector("[data-optional-toggle]").addEventListener("click", (e) => {
          const btn = e.currentTarget;
          const panel = wrapper.querySelector("[data-optional-rows]");
          const isOpen = btn.getAttribute("aria-expanded") === "true";
          btn.setAttribute("aria-expanded", String(!isOpen));
          btn.querySelector(".optional-toggle__icon").textContent = isOpen ? "+" : "−";
          panel.hidden = isOpen;
        });
      });
    }

    getUpsellTotal() {
      let total = 0;
      this.root.querySelectorAll("[data-upsell]:checked").forEach((cb) => {
        total += parseInt(cb.dataset.price, 10) || 0;
      });
      return total;
    }

    renderSummary() {
      const selectedValues = Object.values(this.selections);
      const totalWattage = selectedValues.reduce((sum, s) => sum + (parseFloat(s.wattage) || 0), 0);
      const totalPriceCents =
        selectedValues.reduce((sum, item) => sum + (item.price || 0), 0) +
        this.getUpsellTotal();
      const selectedCount = selectedValues.length;
      
      const requiredIds = this.components.filter((c) => c.required).map((c) => c.id);
      const missingRequired = requiredIds.filter((id) => !this.selections[id]);

      if (this.summaryCountEl) this.summaryCountEl.textContent = String(selectedCount);
      this.summaryTotalEls.forEach((el) => {
        el.textContent = money(totalPriceCents, this.moneyFormat);
      });
      if (this.capsuleTotalEl) this.capsuleTotalEl.textContent = money(totalPriceCents, this.moneyFormat);

      // Update Header Steps
      const hasName = this.buildNameInput && this.buildNameInput.value.trim().length > 0;
      const hasHardware = selectedCount > 0;
      const isComplete = missingRequired.length === 0 && selectedCount > 0;
      const isCompatible = !selectedValues.some(s => s.warning);

      this.headerSteps.forEach(step => {
        const id = step.dataset.stepId;
        step.classList.remove("is-active");
        if (id === "1") step.classList.add("is-active"); // Base state
        if (id === "2" && hasHardware) step.classList.add("is-active");
        if (id === "3" && isComplete && isCompatible) step.classList.add("is-active");
      });

      // Update Wattage Draw (Warnings removed per user request)
      if (this.wattageBox) {
        this.wattageBox.hidden = totalWattage === 0;
        if (this.totalWattageEl) this.totalWattageEl.textContent = String(Math.round(totalWattage));
        if (this.wattageFillEl) {
          const pct = Math.min(100, (totalWattage / 850) * 100);
          this.wattageFillEl.style.width = `${pct}%`;
          this.wattageFillEl.style.background = "";
          this.wattageFillEl.classList.toggle("warning", pct > 80);
        }
      }

      // Check for Certification
      if (this.certifiedBadge) {
        this.certifiedBadge.hidden = !(isComplete && isCompatible);
      }

      this.updateProgressBar();
      this.renderMessages(missingRequired, selectedValues);
    }

    updateProgressBar() {
      const required = this.components.filter((c) => c.required);
      const total = required.length;
      let selectedCount = 0;
      required.forEach((c) => {
        if (this.selections[c.id]) selectedCount++;
      });

      const pct = total > 0 ? Math.min(100, (selectedCount / total) * 100) : 0;
      
      if (this.progressFillEl) this.progressFillEl.style.width = `${pct}%`;
      if (this.progressSelectedEl) this.progressSelectedEl.textContent = String(selectedCount);

      // Update Capsule
      if (this.capsulePercentEl) this.capsulePercentEl.textContent = `${Math.round(pct)}%`;
      if (this.capsuleRingEl) {
        const offset = 94.24 - (pct / 100) * 94.24;
        this.capsuleRingEl.style.strokeDashoffset = offset;
      }

      if (pct === 100 && !this._celebrated) {
        this.fireCelebration();
        this._celebrated = true;
      } else if (pct < 100) {
        this._celebrated = false;
      }
    }

    renderMessages(missingRequired, selectedValues) {
      if (!this.summaryMsgsEl) return;
      const selectedCount = selectedValues.length;

      // Calculate Total Savings
      const totalBase = selectedValues.reduce((sum, item) => sum + (item.price || 0), 0) + this.getUpsellTotal();
      const totalCompare = selectedValues.reduce((sum, item) => sum + (item.compare_at_price || item.price || 0), 0) + this.getUpsellTotal();
      const savings = totalCompare - totalBase;

      // Build per-item breakdown lines
      const savingsItems = selectedValues
        .filter(item => item.compare_at_price && item.compare_at_price > item.price)
        .map(item => {
          const label = escapeHtml(item.title.split(' ').slice(0, 5).join(' '));
          const saved = money(item.compare_at_price - item.price, this.moneyFormat);
          return `<div class="savings-breakdown__line">\u2713 ${label}: ${saved}</div>`;
        });

      if (savings > 0) {
        const pct = Math.round((savings / totalCompare) * 100);
        const hasBreakdown = savingsItems.length > 0;
        this.summaryMsgsEl.innerHTML = `
          <div class="summary-message is-savings" data-savings-msg>
            <span class="savings-icon">\ud83c\udff7\ufe0f</span>
            <span class="savings-text" data-savings-display></span>
            ${hasBreakdown ? '<button type="button" class="savings-toggle" aria-expanded="false" aria-label="See savings breakdown">\u25be Details</button>' : ''}
            ${hasBreakdown ? `<div class="savings-breakdown" hidden>${savingsItems.join('')}</div>` : ''}
          </div>`;
        // Animate count-up from last value
        this._animateSavings(savings, pct);
        // Breakdown toggle
        const toggleBtn = this.summaryMsgsEl.querySelector('.savings-toggle');
        const breakdown = this.summaryMsgsEl.querySelector('.savings-breakdown');
        if (toggleBtn && breakdown) {
          toggleBtn.addEventListener('click', () => {
            const isOpen = !breakdown.hidden;
            breakdown.hidden = isOpen;
            toggleBtn.textContent = isOpen ? '\u25be Details' : '\u25b4 Hide';
            toggleBtn.setAttribute('aria-expanded', String(!isOpen));
          });
        }
      } else {
        // Zero-savings: hide message area completely
        this.summaryMsgsEl.innerHTML = '';
      }

      // Allow checkout as long as at least 1 item is selected
      const canCheckout = selectedCount > 0;
      if (this.addBtn) this.addBtn.disabled = !canCheckout;
      if (this.addToCartLabel) {
        this.addToCartLabel.textContent = canCheckout
          ? `Add ${selectedCount} item${selectedCount > 1 ? 's' : ''} to cart`
          : 'Add All to Cart';
      }
    }

    _animateSavings(targetCents, pct) {
      const el = this.summaryMsgsEl && this.summaryMsgsEl.querySelector('[data-savings-display]');
      if (!el) return;
      const duration = 700;
      const start = performance.now();
      const startVal = this._lastSavings || 0;
      this._lastSavings = targetCents;
      const animate = (now) => {
        const elapsed = Math.min(now - start, duration);
        const t = elapsed / duration;
        const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
        const current = Math.round(startVal + (targetCents - startVal) * eased);
        el.textContent = `You saved ${money(current, this.moneyFormat)} (${pct}% USD)`;
        if (elapsed < duration) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }

    renderProgress() {
      if (!this.progressEl) return;
      const requiredIds = this.components.filter((c) => c.required).map((c) => c.id);
      const total = requiredIds.length;
      const selected = requiredIds.filter((id) => !!this.selections[id]).length;
      this.progressTotalEl.textContent = String(total);
      this.progressSelectedEl.textContent = String(selected);
      const pct = total > 0 ? Math.min(100, (selected / total) * 100) : 0;
      this.progressFillEl.style.width = `${pct}%`;

      if (pct === 100 && !this._celebrated) {
        this.fireCelebration();
        this._celebrated = true;
      } else if (pct < 100) {
        this._celebrated = false;
      }
    }

    fireCelebration() {
      if (!window.confetti) {
        const script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/npm/canvas-confetti@1.6.0/dist/confetti.browser.min.js";
        script.onload = () => window.confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 }, zIndex: 3000 });
        document.head.appendChild(script);
      } else {
        window.confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 }, zIndex: 3000 });
      }
    }

    // ----- Shareable URLs (Phase 4) -----

    updateQueryString() {
      if (!this.shareableUrls || !window.history || !window.URLSearchParams) return;
      const params = new URLSearchParams(window.location.search);
      // Clear prior pc builder params by iterating known component ids.
      this.components.forEach((c) => params.delete(c.id));
      params.delete("name");

      Object.entries(this.selections).forEach(([id, sel]) => {
        params.set(id, String(sel.productId));
      });
      if (this.buildNameInput && this.buildNameInput.value) {
        params.set("name", this.buildNameInput.value);
      }
      const newUrl =
        window.location.pathname +
        (params.toString() ? "?" + params.toString() : "") +
        window.location.hash;
      try {
        history.replaceState(null, "", newUrl);
      } catch (e) {
        /* ignore */
      }
    }

    hydrateFromQueryString() {
      if (!this.shareableUrls || !window.URLSearchParams) return;
      const params = new URLSearchParams(window.location.search);
      
      const savedName = params.get("name");
      if (savedName && this.buildNameInput) {
        this.buildNameInput.value = savedName;
      }

      this.components.forEach((comp) => {
        const pid = params.get(comp.id);
        if (!pid) return;
        const products = this.productsByComponent[comp.id] || [];
        const product = products.find((p) => String(p.id) === String(pid));
        if (!product) return;
        const firstAvailable =
          (product.variants || []).find((v) => v.available) ||
          (product.variants && product.variants[0]);
        if (!firstAvailable) return;
        this.selections[comp.id] = {
          productId: product.id,
          variantId: firstAvailable.id,
          title: product.title,
          image: product.image,
          price: firstAvailable.price,
          compare_at_price: firstAvailable.compare_at_price || null,
          socket: product.socket || null,
          ram_type: product.ram_type || null,
          wattage: product.wattage || 0,
          handle: product.handle,
          url: product.url,
        };
      });
      this.refreshCompatibility();
    }

    // ----- Save/load integration -----

    // Called by pc-builder-saves.js applyBuildToUI (public API).
    // build shape: { cpu: "<product title or id>", motherboard: "...", ... }
    applyBuildFromSaves(build) {
      if (!build || typeof build !== "object") return;
      this.selections = {};
      Object.entries(build).forEach(([componentId, value]) => {
        if (!value) return;
        const products = this.productsByComponent[componentId] || [];
        // Match by ID first (numeric string), then title.
        let product = products.find((p) => String(p.id) === String(value));
        if (!product) {
          product = products.find(
            (p) => p.title === value || (value && p.title.indexOf(value) !== -1)
          );
        }
        if (!product) return;
        const firstAvailable =
          (product.variants || []).find((v) => v.available) ||
          (product.variants && product.variants[0]);
        if (!firstAvailable) return;
        this.selections[componentId] = {
          productId: product.id,
          variantId: firstAvailable.id,
          title: product.title,
          image: product.image,
          price: firstAvailable.price,
          compare_at_price: firstAvailable.compare_at_price || null,
          socket: product.socket || null,
          ram_type: product.ram_type || null,
          wattage: product.wattage || 0,
          handle: product.handle,
          url: product.url,
        };
      });
      this.refreshCompatibility();
      this.render();
      this.updateQueryString();
    }

    // ----- Analytics (Phase 5) -----
    
    _trackEvent(eventName, params) {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
      } else if (typeof window.dataLayer !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: eventName, ...params });
      } else {
        // Fallback or debug log
        console.debug(`[pc-builder] Tracked event: ${eventName}`, params);
      }
    }

    // ----- Add to cart (Phase 4 - cart drawer integration) -----

    async addAllToCart() {
      const selectedCount = Object.keys(this.selections).length;
      if (selectedCount === 0) return;

      // Check for missing required components
      const required = this.components.filter((c) => c.required);
      const missing = required.filter((c) => !this.selections[c.id]);

      if (missing.length > 0) {
        this.showConfirmModal(missing.map(m => m.label));
        return;
      }

      this.performAddToCart();
    }

    showConfirmModal(missingLabels) {
      const modal = this.root.querySelector("[data-confirm-modal-overlay]");
      if (!modal) {
        // Fallback to basic confirm if modal setup failed
        if (confirm(`Are you sure you don't need missing parts? Missing: ${missingLabels.join(", ")}`)) {
          this.performAddToCart();
        }
        return;
      }

      const list = modal.querySelector("[data-confirm-modal-parts]");
      if (list) list.textContent = missingLabels.join(", ");

      modal.classList.add("is-open");
      modal.setAttribute("aria-hidden", "false");

      const continueBtn = modal.querySelector("[data-confirm-modal-continue]");
      const goBackBtn = modal.querySelector("[data-confirm-modal-go-back]");

      // Focus trap for confirm modal (P7)
      const confirmModalEl = modal.querySelector(".modal--confirm") || modal;
      const releaseTrap = focusTrap(confirmModalEl);
      // Move focus to the first button
      const firstFocus = confirmModalEl.querySelector("button");
      if (firstFocus) firstFocus.focus();

      const cleanup = () => {
        modal.classList.remove("is-open");
        modal.setAttribute("aria-hidden", "true");
        releaseTrap();
        continueBtn.removeEventListener("click", onContinue);
        goBackBtn.removeEventListener("click", onBack);
      };

      const onContinue = () => {
        cleanup();
        this._trackEvent("checkout_anyway", { missing_components: missingLabels });
        this.performAddToCart();
      };
      const onBack = () => {
        cleanup();
      };

      continueBtn.addEventListener("click", onContinue);
      goBackBtn.addEventListener("click", onBack);
    }

    async performAddToCart() {
      const components = Object.values(this.selections);
      const upsellItems = Array.from(
        this.root.querySelectorAll("[data-upsell]:checked")
      ).map((cb) => ({ id: parseInt(cb.dataset.variantId, 10), quantity: 1 }));
      const items = components
        .map((c) => ({ id: parseInt(c.variantId, 10), quantity: 1 }))
        .concat(upsellItems);

      if (this.addBtn) { this.addBtn.classList.add("is-loading"); this.addBtn.disabled = true; }
      this.hideToast();

      const cartDrawer = document.querySelector("cart-drawer");
      const body = { items };
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === "function") {
        body.sections = cartDrawer.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }

      try {
        const res = await fetch((window.routes && window.routes.cart_add_url) || "/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/javascript",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok || data.status) {
          throw new Error(data.description || data.message || "Failed to add to cart");
        }
        if (cartDrawer && typeof cartDrawer.renderContents === "function") {
          cartDrawer.renderContents(data);
        } else {
          this.showToast("Items added to cart.", "success");
          setTimeout(() => {
            window.location.href = (window.routes && window.routes.cart_url) || "/cart";
          }, 600);
        }
        if (typeof window.publish === "function" && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: "pc-builder",
          });
        }
        
        // P5: GA4 tracking - items added to cart
        const gaItems = components.map(c => ({
          item_id: String(c.productId),
          item_name: c.title,
          price: (c.price / 100),
          quantity: 1
        }));
        this._trackEvent("add_to_cart", {
          currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
          value: components.reduce((acc, c) => acc + (c.price/100), 0),
          items: gaItems
        });
        
      } catch (err) {
        console.error("[pc-builder] add to cart failed", err);
        this.showToast(err.message || "Could not add items to cart.", "error");
      } finally {
        if (this.addBtn) { this.addBtn.classList.remove("is-loading"); this.addBtn.disabled = false; }
        this.renderSummary();
      }
    }

    showToast(message, kind) {
      if (!this.summaryToastEl) return;
      this.summaryToastEl.textContent = message;
      this.summaryToastEl.className =
        "summary-toast " + (kind === "success" ? "is-success" : "is-error");
      this.summaryToastEl.hidden = false;
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => this.hideToast(), 4000);
    }

    hideToast() {
      if (!this.summaryToastEl) return;
      this.summaryToastEl.hidden = true;
    }
  }

  // ----- Bootstrap -----

  function init() {
    document.querySelectorAll(SECTION_SELECTOR).forEach((root) => {
      if (root._pcBuilderInit) return;
      root._pcBuilderInit = true;
      try {
        new PCBuilder(root);
      } catch (e) {
        console.error("[pc-builder] init failed", e);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Re-init when the theme editor re-renders the section.
  document.addEventListener("shopify:section:load", (e) => {
    const target = e.target;
    if (target && target.querySelector && target.querySelector(SECTION_SELECTOR)) {
      init();
    }
  });
})();
